import { Component, EventEmitter, Input, OnChanges, OnInit, Output } from '@angular/core';
import { ControlsOf, FormGroupType } from '@app/core/utils/formgroup-type';
import { AddressDto } from '@app/api/nts';
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { InputDirective } from '@shared/components/input/input/input.directive';
import { InputGroupComponent } from '@shared/components/input/input-group/input-group.component';
import { LabelDirective } from '@shared/components/input/label/label.directive';
import { TranslatePipe } from '@ngx-translate/core';
import { NgClass } from '@angular/common';
import { SearchConfig } from '@shared/components/input/search/search-config.interface';
import { AutocompleteService } from '@app/core/services/autocomplete.service';
import { catchError, Observable, of } from 'rxjs';
import { SearchResult, SearchResultCollection } from '@shared/components/input/search/search-result.interface';
import { map } from 'rxjs/operators';
import { mapDarResults } from '@app/core/utils/map-dar-results';
import { patchAddress } from '@app/core/utils/patch-address';
import { ToastService } from '@app/core/services/toast.service';
import { AlertService } from '@app/core/services/alert.service';
import { SearchComponent } from '@shared/components/input/search/search.component';
import { debounce } from '@app/core/utils/debounce';

@Component({
  selector: 'app-address',
  imports: [
    ReactiveFormsModule,
    InputDirective,
    InputGroupComponent,
    LabelDirective,
    TranslatePipe,
    NgClass,
    SearchComponent
  ],
  templateUrl: './address.component.html',
  styleUrl: './address.component.css'
})
export class AddressComponent implements OnInit, OnChanges {
  public form: FormGroupType<AddressDto> = new FormGroup<ControlsOf<AddressDto>>({
    streetName: new FormControl<string | null>({ value: null, disabled: true }),
    streetCode: new FormControl<string | null>({ value: null, disabled: true }),
    buildingNumber: new FormControl<string | null>({ value: null, disabled: true }),
    floorIdentification: new FormControl<string | null>({ value: null, disabled: true }),
    roomIdentification: new FormControl<string | null>({ value: null, disabled: true }),
    subDivisionName: new FormControl<string | null>({ value: null, disabled: true }),
    postalCode: new FormControl<string | null>({ value: null, disabled: true }),
    cityName: new FormControl<string | null>({ value: null, disabled: true }),
    municipalityCode: new FormControl<string | null>({ value: null, disabled: true }),
    country: new FormControl<string | null>({ value: null, disabled: true }),
    darReference: new FormControl<string | null>({ value: null, disabled: true }),
  });

  @Input() public address?: AddressDto | null = {};
  @Output() public addressChange: EventEmitter<AddressDto> = new EventEmitter();

  @Input() public layout: 'linear' | 'horizontal' = 'linear';

  /** Search Config **/
  public searchConfig: SearchConfig = {
    hotkeys: false,
    standalone: true,
    autoFocus: false,
    navigateUrl: undefined,
    callBackFunction: (value: string) => this.searchQuery(value),
  }

  public advancedInput: boolean = false;
  public addressFetched: boolean = false;

  constructor(
    private readonly autocompleteService: AutocompleteService,
    private readonly toastService: ToastService,
    private readonly alertService: AlertService,
  ) { }

  public ngOnInit(): void {
    const logDebounced = debounce((value: any) => {
      this.addressChange.emit(this.form.value);
    }, 300);

    this.form.valueChanges.subscribe(logDebounced);
  }

  public ngOnChanges(): void {
    if (this.address) {
      this.form.patchValue(this.address);
    }
  }

  /** Search Query **/
  public searchQuery(value: string): Observable<SearchResultCollection[]> {
    if (value.length > 2) {
      return this.autocompleteService.searchAddress(value)
        .pipe(
          map((results: any) => mapDarResults(results)),
          catchError(
            (error) => {
              console.error('Error searching address:', error);
              return of([]);
            }
          )
        )
    }
    return of([]);
  }

  public addressSelected(event: SearchResult): void {
    if (event.id) {
      this.autocompleteService.getById(event.id).subscribe((addr: any) => {
        /** Patch Address **/
        patchAddress(this.form, addr);

        /** Adress Fetched **/
        this.addressFetched = true;
      })
    }
  }

  public sanitizeAddress(): void {
    this.form.markAsTouched();

    if (this.form.valid) {
      const addr = `${this.form.get('streetName')?.value} ${this.form.get('buildingNumber')?.value} ${this.form.get('postalCode')?.value} ${this.form.get('cityName')?.value ?? ''}`;
      this.autocompleteService.sanitizeAddress(addr.trim()).subscribe((results) => {
        if (results.kategori === 'A' || results.kategori === 'B') {
          this.autocompleteService.getById(results.resultater[0].adresse.id).subscribe((data) => {
            if (data) {
              /** Necessary Evil **/
              const doorReference = 'dør';

              this.form.patchValue({
                streetName: data.adgangsadresse.vejstykke.navn,
                streetCode: data.adgangsadresse.vejstykke.kode,
                buildingNumber: data.adgangsadresse.husnr,
                floorIdentification: data.etage,
                roomIdentification: data[doorReference],
                subDivisionName: data.adgangsadresse.supplerendebynavn,
                postalCode: data.adgangsadresse.postnummer.nr,
                cityName: data.adgangsadresse.postnummer.navn,
                municipalityCode: data.adgangsadresse.kommune.kode,
                darReference: data.id,
                country: 'DK'
              });

              this.toastService.show('address.addresssanitized');
            }
          });
        } else {
          this.alertService.open(this.layout === 'linear' ? 'modal' : 'header', 'danger', 'address.notunique.');
        }
      });
    }
  }

  public activateSearch(): void {
    this.advancedInput = false;
    this.addressFetched = false;

    this.form.disable();
    this.form.get('streetName')?.removeValidators(Validators.required);
    this.form.get('buildingNumber')?.removeValidators(Validators.required);
    this.form.get('postalCode')?.removeValidators(Validators.required);
    this.form.get('country')?.removeValidators(Validators.required);
  }

  public activateAdvanced(): void {
    this.advancedInput = true;
    this.addressFetched = false;

    this.form.enable();
    this.form.get('streetName')?.setValidators(Validators.required);
    this.form.get('buildingNumber')?.setValidators(Validators.required);
    this.form.get('postalCode')?.setValidators(Validators.required);
    this.form.get('country')?.setValidators(Validators.required);
  }
}
